// src/data/menuData.ts

export type MenuCategory = 
  | 'bier' 
  | 'alkoholfrei' 
  | 'heisse-getraenke' 
  | 'cocktails' 
  | 'longdrinks-wein'
  | 'angebote'
  | 'tapas'
  | 'desserts';

export type MenuTag = 'vegan' | 'fleisch' | 'fisch' | 'vegetarisch' | 'scharf' | 'glutenfrei' | 'alkoholfrei';

export interface MenuItem {
  id: string;
  name: string;
  description: string;
  price: number;
  category: MenuCategory;
  subCategory?: 'fleisch' | 'fisch' | 'veggie';
  tags: MenuTag[];
  image?: string;
  isSpecial?: boolean;
}

export interface MenuSection {
  id: MenuCategory;
  title: string;
  subtitle: string;
  items: MenuItem[];
}

export const menuCategories: { id: MenuCategory; label: string }[] = [
  { id: 'angebote', label: 'angebote' },
  { id: 'tapas', label: 'tapas' },
  { id: 'desserts', label: 'desserts' },
  { id: 'cocktails', label: 'cocktails' },
  { id: 'longdrinks-wein', label: 'longdrinks & wein' },
  { id: 'bier', label: 'bier' },
  { id: 'alkoholfrei', label: 'alkoholfrei' },
  { id: 'heisse-getraenke', label: 'heiße getränke' },
];

export const menuData: MenuSection[] = [
  // angebote
  {
    id: 'angebote',
    title: 'la vida angebote',
    subtitle: 'unsere besonderen menü-kombinationen',
    items: [
      {
        id: 'angebot-1',
        name: 'la vida cuatro',
        description: '4 tapas nach wahl (exkl. ausnahmen), 1 portion brot',
        price: 19.90,
        category: 'angebote',
        tags: ['vegan', 'vegetarisch', 'fleisch', 'fisch'],
        isSpecial: true,
      },
      {
        id: 'angebot-2',
        name: 'la vida menü',
        description: '6 tapas nach wahl (exkl. ausnahmen), 1 flasche hauswein, 1 großes wasser, 2 portionen brot',
        price: 49.90,
        category: 'angebote',
        tags: ['vegan', 'vegetarisch', 'fleisch', 'fisch'],
        isSpecial: true,
      },
      {
        id: 'angebot-3',
        name: 'gambas pfanne',
        description: 'für 2 personen, mit knoblauch, butter, chili und brot',
        price: 17.90,
        category: 'angebote',
        tags: ['fisch', 'scharf'],
        isSpecial: true,
      },
    ],
  },

  // tapas (alle zusammen)
  {
    id: 'tapas',
    title: 'tapas',
    subtitle: 'kleine köstlichkeiten zum teilen - fleisch, fisch, vegan & vegetarisch',
    items: [
      // === fleisch ===
      {
        id: 'fleisch-1',
        name: 'gostê birastî',
        description: 'rindfleisch nach türkischer art',
        price: 4.90,
        category: 'tapas',
        subCategory: 'fleisch',
        tags: ['fleisch'],
      },
      {
        id: 'fleisch-2',
        name: 'chorizo',
        description: 'pikante spanische paprikawurst',
        price: 4.90,
        category: 'tapas',
        subCategory: 'fleisch',
        tags: ['fleisch', 'scharf'],
      },
      {
        id: 'fleisch-3',
        name: 'hähnchenbruststreifen',
        description: 'zarte hähnchenbrust in würziger marinade',
        price: 4.90,
        category: 'tapas',
        subCategory: 'fleisch',
        tags: ['fleisch'],
      },
      {
        id: 'fleisch-4',
        name: 'jamón serrano',
        description: 'spanischer schinken, luftgetrocknet',
        price: 4.90,
        category: 'tapas',
        subCategory: 'fleisch',
        tags: ['fleisch'],
      },
      {
        id: 'fleisch-5',
        name: 'datteln im speckmantel',
        description: 'süße datteln umhüllt von knusprigem speck',
        price: 4.90,
        category: 'tapas',
        subCategory: 'fleisch',
        tags: ['fleisch'],
      },
      {
        id: 'fleisch-6',
        name: 'currywurst',
        description: 'deutsche klassiker mit hausgemachter currysauce',
        price: 4.90,
        category: 'tapas',
        subCategory: 'fleisch',
        tags: ['fleisch', 'scharf'],
      },
      {
        id: 'fleisch-7',
        name: 'chicken wings',
        description: 'würzige hähnchenflügel, wahlweise mit knusper-panade',
        price: 4.90,
        category: 'tapas',
        subCategory: 'fleisch',
        tags: ['fleisch', 'scharf'],
      },
      {
        id: 'fleisch-8',
        name: 'crunchy wings',
        description: 'extra knusprige hähnchenflügel',
        price: 6.50,
        category: 'tapas',
        subCategory: 'fleisch',
        tags: ['fleisch', 'scharf'],
      },
      {
        id: 'fleisch-9',
        name: 'albondigas',
        description: 'hausgemachte fleischbällchen in tomatensauce',
        price: 4.90,
        category: 'tapas',
        subCategory: 'fleisch',
        tags: ['fleisch'],
      },
      {
        id: 'fleisch-10',
        name: 'hähnchen döner',
        description: 'hähnchenfleisch vom spieß, mariniert nach türkischer art',
        price: 4.90,
        category: 'tapas',
        subCategory: 'fleisch',
        tags: ['fleisch'],
      },
      // === fisch ===
      {
        id: 'fisch-1',
        name: 'gambas al ajillo',
        description: 'garnelen in knoblauch-öl mit chili und frischem brot',
        price: 8.90,
        category: 'tapas',
        subCategory: 'fisch',
        tags: ['fisch', 'scharf'],
      },
      {
        id: 'fisch-2',
        name: 'kalamarakia',
        description: 'knusprig frittierte tintenfischringe mit zitrone',
        price: 6.90,
        category: 'tapas',
        subCategory: 'fisch',
        tags: ['fisch'],
      },
      {
        id: 'fisch-3',
        name: 'frutos do mar',
        description: 'gemischte meeresfrüchte in würziger sauce',
        price: 6.50,
        category: 'tapas',
        subCategory: 'fisch',
        tags: ['fisch'],
      },
      {
        id: 'fisch-4',
        name: 'hamsi',
        description: 'frittierte sardellen, knusprig und goldbraun',
        price: 5.50,
        category: 'tapas',
        subCategory: 'fisch',
        tags: ['fisch'],
      },
      // === vegan & veggie ===
      // dips
      {
        id: 'veggie-1',
        name: 'hummus',
        description: 'cremiges kichererbsenpüree mit tahini, zitrone und olivenöl',
        price: 4.90,
        category: 'tapas',
        subCategory: 'veggie',
        tags: ['vegan'],
      },
      {
        id: 'veggie-2',
        name: 'baba ghanoush',
        description: 'geräuchertes auberginenpüree mit sesam und knoblauch',
        price: 4.90,
        category: 'tapas',
        subCategory: 'veggie',
        tags: ['vegan'],
      },
      {
        id: 'veggie-3',
        name: 'aioli',
        description: 'spanische knoblauchmayonnaise',
        price: 4.90,
        category: 'tapas',
        subCategory: 'veggie',
        tags: ['vegetarisch'],
      },
      {
        id: 'veggie-4',
        name: 'haydari',
        description: 'türkischer joghurt-dip mit knoblauch und kräutern',
        price: 4.90,
        category: 'tapas',
        subCategory: 'veggie',
        tags: ['vegetarisch'],
      },
      {
        id: 'veggie-5',
        name: 'tabouleh',
        description: 'bulgur-salat mit petersilie, minze, tomaten und zitrone',
        price: 4.90,
        category: 'tapas',
        subCategory: 'veggie',
        tags: ['vegan'],
      },
      {
        id: 'veggie-6',
        name: 'salat shirazi',
        description: 'persischer salat mit gurken, tomaten und zitrone',
        price: 4.90,
        category: 'tapas',
        subCategory: 'veggie',
        tags: ['vegan'],
      },
      {
        id: 'veggie-7',
        name: 'griechischer bohnensalat',
        description: 'weiße bohnen mit oliven, zwiebeln und kräutern',
        price: 4.90,
        category: 'tapas',
        subCategory: 'veggie',
        tags: ['vegan'],
      },
      // warmes
      {
        id: 'veggie-8',
        name: 'linsensuppe',
        description: 'herzhafte rote linsensuppe mit orientalischen gewürzen',
        price: 4.90,
        category: 'tapas',
        subCategory: 'veggie',
        tags: ['vegan'],
      },
      {
        id: 'veggie-9',
        name: 'pimientos de padrón',
        description: 'gegrillte grüne paprikaschoten mit meersalz',
        price: 4.90,
        category: 'tapas',
        subCategory: 'veggie',
        tags: ['vegan'],
      },
      {
        id: 'veggie-10',
        name: 'bruschetta',
        description: 'geröstetes brot mit tomaten, knoblauch und basilikum',
        price: 4.90,
        category: 'tapas',
        subCategory: 'veggie',
        tags: ['vegan'],
      },
      {
        id: 'veggie-11',
        name: 'falafel',
        description: 'knusprige kichererbsenbällchen mit sesam',
        price: 4.90,
        category: 'tapas',
        subCategory: 'veggie',
        tags: ['vegan'],
      },
      {
        id: 'veggie-12',
        name: 'patatas bravas',
        description: 'knusprige kartoffelwürfel mit würziger tomaten-paprika-sauce',
        price: 4.90,
        category: 'tapas',
        subCategory: 'veggie',
        tags: ['vegan', 'scharf'],
      },
      {
        id: 'veggie-13',
        name: 'pimientos',
        description: 'gegrillte paprika in öl',
        price: 4.90,
        category: 'tapas',
        subCategory: 'veggie',
        tags: ['vegan'],
      },
      {
        id: 'veggie-14',
        name: 'champignons',
        description: 'gebratene pilze mit knoblauch und kräutern',
        price: 4.90,
        category: 'tapas',
        subCategory: 'veggie',
        tags: ['vegan'],
      },
      // gemüse
      {
        id: 'veggie-15',
        name: 'gebratene grüne bohnen',
        description: 'mit tomaten und zwiebeln geschmort',
        price: 4.90,
        category: 'tapas',
        subCategory: 'veggie',
        tags: ['vegan'],
      },
      {
        id: 'veggie-16',
        name: 'nivik',
        description: 'türkischer kichererbsen-spinat-eintopf',
        price: 4.90,
        category: 'tapas',
        subCategory: 'veggie',
        tags: ['vegan'],
      },
      {
        id: 'veggie-17',
        name: 'kartol',
        description: 'türkischer kartoffelstampf mit gewürzen',
        price: 4.90,
        category: 'tapas',
        subCategory: 'veggie',
        tags: ['vegan'],
      },
      {
        id: 'veggie-18',
        name: 'gefüllte weinblätter',
        description: 'mit reis, minze und zitrone gedünstet',
        price: 4.90,
        category: 'tapas',
        subCategory: 'veggie',
        tags: ['vegan'],
      },
      // käse
      {
        id: 'veggie-19',
        name: 'manchego',
        description: 'spanischer schafskäse, gereift',
        price: 4.90,
        category: 'tapas',
        subCategory: 'veggie',
        tags: ['vegetarisch'],
      },
      {
        id: 'veggie-20',
        name: 'parmigiano',
        description: 'italienischer hartkäse, gereift',
        price: 4.90,
        category: 'tapas',
        subCategory: 'veggie',
        tags: ['vegetarisch'],
      },
      {
        id: 'veggie-21',
        name: 'halloumi',
        description: 'zypriotischer grillkäse, knusprig gebraten',
        price: 4.90,
        category: 'tapas',
        subCategory: 'veggie',
        tags: ['vegetarisch'],
      },
      {
        id: 'veggie-22',
        name: 'feta saganaki',
        description: 'gebackener feta mit sesamkruste',
        price: 4.90,
        category: 'tapas',
        subCategory: 'veggie',
        tags: ['vegetarisch'],
      },
      {
        id: 'veggie-23',
        name: 'sigara böregi',
        description: 'knusprige teigröllchen mit schafskäsefüllung',
        price: 4.90,
        category: 'tapas',
        subCategory: 'veggie',
        tags: ['vegetarisch'],
      },
    ],
  },

  // desserts
  {
    id: 'desserts',
    title: 'desserts',
    subtitle: 'süße verführungen zum abschluss',
    items: [
      {
        id: 'dessert-1',
        name: 'gemischte käseplatte',
        description: 'auswahl verschiedener käsesorten mit brot und feigen',
        price: 12.90,
        category: 'desserts',
        tags: ['vegetarisch'],
      },
      {
        id: 'dessert-2',
        name: 'affogato al caffé',
        description: 'vanilleeis übergossen mit heißem espresso',
        price: 4.90,
        category: 'desserts',
        tags: ['vegetarisch'],
      },
      {
        id: 'dessert-3',
        name: 'gelato',
        description: '3 kugeln italienisches eis, geschmack nach wahl',
        price: 4.90,
        category: 'desserts',
        tags: ['vegetarisch'],
      },
      {
        id: 'dessert-4',
        name: 'sütlaç',
        description: 'türkischer ofenmilchreis mit zimt',
        price: 4.90,
        category: 'desserts',
        tags: ['vegetarisch', 'glutenfrei'],
      },
      {
        id: 'dessert-5',
        name: 'schokoladensoufflé',
        description: 'warmes schokoladenküchlein mit flüssigem kern',
        price: 4.90,
        category: 'desserts',
        tags: ['vegetarisch'],
      },
    ],
  },

  // bier
  {
    id: 'bier',
    title: 'bier',
    subtitle: 'vom fass und aus der flasche',
    items: [
      {
        id: 'bier-1',
        name: 'veltins (0,3l)',
        description: 'deutsches pilsener vom fass',
        price: 3.20,
        category: 'bier',
        tags: ['vegan'],
      },
      {
        id: 'bier-2',
        name: 'veltins (0,5l)',
        description: 'deutsches pilsener vom fass',
        price: 4.90,
        category: 'bier',
        tags: ['vegan'],
      },
      {
        id: 'bier-3',
        name: 'alster (0,3l)',
        description: 'bier mit limonade gemischt (shandy)',
        price: 3.20,
        category: 'bier',
        tags: ['vegan'],
      },
      {
        id: 'bier-4',
        name: 'alster (0,5l)',
        description: 'bier mit limonade gemischt (shandy)',
        price: 4.90,
        category: 'bier',
        tags: ['vegan'],
      },
      {
        id: 'bier-5',
        name: 'radler (0,3l)',
        description: 'bier mit zitronenlimonade',
        price: 3.20,
        category: 'bier',
        tags: ['vegan'],
      },
      {
        id: 'bier-6',
        name: 'radler (0,5l)',
        description: 'bier mit zitronenlimonade',
        price: 4.90,
        category: 'bier',
        tags: ['vegan'],
      },
      {
        id: 'bier-7',
        name: 'krefelder (0,3l)',
        description: 'altbiermischgetränk mit coca-cola',
        price: 3.20,
        category: 'bier',
        tags: ['vegan'],
      },
      {
        id: 'bier-8',
        name: 'krefelder (0,5l)',
        description: 'altbiermischgetränk mit coca-cola',
        price: 4.90,
        category: 'bier',
        tags: ['vegan'],
      },
      {
        id: 'bier-9',
        name: 'malzbier',
        description: 'süßes, dunkles bier',
        price: 3.20,
        category: 'bier',
        tags: ['vegan'],
      },
      {
        id: 'bier-10',
        name: 'veltins alkoholfrei',
        description: 'alkoholfreies pilsener',
        price: 3.20,
        category: 'bier',
        tags: ['vegan', 'alkoholfrei'],
      },
      {
        id: 'bier-11',
        name: "maisel's Weiße",
        description: 'bayerisches weizenbier',
        price: 4.90,
        category: 'bier',
        tags: ['vegan'],
      },
      {
        id: 'bier-12',
        name: "maisel's Weiße Alkoholfrei",
        description: 'alkoholfreies weizenbier',
        price: 4.90,
        category: 'bier',
        tags: ['vegan', 'alkoholfrei'],
      },
      {
        id: 'bier-13',
        name: 'estrella galicia',
        description: 'spanisches premium-lagerbier',
        price: 3.50,
        category: 'bier',
        tags: ['vegan'],
      },
    ],
  },

  // alkoholfreie getränke
  {
    id: 'alkoholfrei',
    title: 'alkoholfreie getränke',
    subtitle: 'erfrischende alternativen',
    items: [
      {
        id: 'alkoholfrei-1',
        name: 'aqua still (0,25l)',
        description: 'natürliches mineralwasser',
        price: 2.80,
        category: 'alkoholfrei',
        tags: ['vegan', 'alkoholfrei'],
      },
      {
        id: 'alkoholfrei-2',
        name: 'aqua medium (0,25l)',
        description: 'natürliches mineralwasser mit kohlensäure',
        price: 2.80,
        category: 'alkoholfrei',
        tags: ['vegan', 'alkoholfrei'],
      },
      {
        id: 'alkoholfrei-3',
        name: 'aqua still (0,75l)',
        description: 'große flasche natürliches mineralwasser',
        price: 5.90,
        category: 'alkoholfrei',
        tags: ['vegan', 'alkoholfrei'],
      },
      {
        id: 'alkoholfrei-4',
        name: 'aqua medium (0,75l)',
        description: 'große flasche mineralwasser mit kohlensäure',
        price: 5.90,
        category: 'alkoholfrei',
        tags: ['vegan', 'alkoholfrei'],
      },
      {
        id: 'alkoholfrei-5',
        name: 'coca-cola classic (0,2l)',
        description: 'das klassische erfrischungsgetränk',
        price: 2.80,
        category: 'alkoholfrei',
        tags: ['vegan', 'alkoholfrei'],
      },
      {
        id: 'alkoholfrei-6',
        name: 'coca-cola zero (0,2l)',
        description: 'cola ohne zucker',
        price: 2.80,
        category: 'alkoholfrei',
        tags: ['vegan', 'alkoholfrei'],
      },
      {
        id: 'alkoholfrei-7',
        name: 'fanta (0,2l)',
        description: 'orangenlimonade',
        price: 2.80,
        category: 'alkoholfrei',
        tags: ['vegan', 'alkoholfrei'],
      },
      {
        id: 'alkoholfrei-8',
        name: 'sprite (0,2l)',
        description: 'zitronen-limonade',
        price: 2.80,
        category: 'alkoholfrei',
        tags: ['vegan', 'alkoholfrei'],
      },
      {
        id: 'alkoholfrei-9',
        name: 'coca-cola classic (0,5l)',
        description: 'große portion cola',
        price: 4.90,
        category: 'alkoholfrei',
        tags: ['vegan', 'alkoholfrei'],
      },
      {
        id: 'alkoholfrei-10',
        name: 'coca-cola zero (0,5l)',
        description: 'große portion cola ohne zucker',
        price: 4.90,
        category: 'alkoholfrei',
        tags: ['vegan', 'alkoholfrei'],
      },
      {
        id: 'alkoholfrei-11',
        name: 'fanta (0,5l)',
        description: 'große portion orangenlimonade',
        price: 4.90,
        category: 'alkoholfrei',
        tags: ['vegan', 'alkoholfrei'],
      },
      {
        id: 'alkoholfrei-12',
        name: 'sprite (0,5l)',
        description: 'große portion zitronen-limonade',
        price: 4.90,
        category: 'alkoholfrei',
        tags: ['vegan', 'alkoholfrei'],
      },
      {
        id: 'alkoholfrei-13',
        name: 'schweppes tonic water (0,2l)',
        description: 'klassisches tonic',
        price: 3.20,
        category: 'alkoholfrei',
        tags: ['vegan', 'alkoholfrei'],
      },
      {
        id: 'alkoholfrei-14',
        name: 'schweppes bitter lemon (0,2l)',
        description: 'bittere zitronenlimonade',
        price: 3.20,
        category: 'alkoholfrei',
        tags: ['vegan', 'alkoholfrei'],
      },
      {
        id: 'alkoholfrei-15',
        name: 'schweppes ginger ale (0,2l)',
        description: 'ingwer-limonade',
        price: 3.20,
        category: 'alkoholfrei',
        tags: ['vegan', 'alkoholfrei'],
      },
      {
        id: 'alkoholfrei-16',
        name: 'apfelsaft (0,2l)',
        description: 'direktsaft, naturtrüb',
        price: 3.20,
        category: 'alkoholfrei',
        tags: ['vegan', 'alkoholfrei'],
      },
      {
        id: 'alkoholfrei-17',
        name: 'orangensaft (0,2l)',
        description: 'direktsaft',
        price: 3.20,
        category: 'alkoholfrei',
        tags: ['vegan', 'alkoholfrei'],
      },
      {
        id: 'alkoholfrei-18',
        name: 'ananassaft (0,2l)',
        description: 'fruchtiger ananassaft',
        price: 3.20,
        category: 'alkoholfrei',
        tags: ['vegan', 'alkoholfrei'],
      },
      {
        id: 'alkoholfrei-19',
        name: 'mangosaft (0,2l)',
        description: 'exotischer mangosaft',
        price: 3.20,
        category: 'alkoholfrei',
        tags: ['vegan', 'alkoholfrei'],
      },
      {
        id: 'alkoholfrei-20',
        name: 'maracujasaft (0,2l)',
        description: 'fruchtiger passionsfruchtsaft',
        price: 3.20,
        category: 'alkoholfrei',
        tags: ['vegan', 'alkoholfrei'],
      },
      {
        id: 'alkoholfrei-21',
        name: 'apfelschorle (0,2l)',
        description: 'saft mit sprudelwasser',
        price: 3.20,
        category: 'alkoholfrei',
        tags: ['vegan', 'alkoholfrei'],
      },
      {
        id: 'alkoholfrei-22',
        name: 'apfelschorle (0,5l)',
        description: 'große portion saft mit sprudelwasser',
        price: 5.20,
        category: 'alkoholfrei',
        tags: ['vegan', 'alkoholfrei'],
      },
      {
        id: 'alkoholfrei-23',
        name: 'orangenschorle (0,2l)',
        description: 'saft mit sprudelwasser',
        price: 3.20,
        category: 'alkoholfrei',
        tags: ['vegan', 'alkoholfrei'],
      },
      {
        id: 'alkoholfrei-24',
        name: 'orangenschorle (0,5l)',
        description: 'große portion saft mit sprudelwasser',
        price: 5.20,
        category: 'alkoholfrei',
        tags: ['vegan', 'alkoholfrei'],
      },
    ],
  },

  // heisse getränke
  {
    id: 'heisse-getraenke',
    title: 'heiße getränke',
    subtitle: 'kaffee, kakao und tee',
    items: [
      {
        id: 'kaffee-1',
        name: 'cafe crema',
        description: 'milder, langer kaffee',
        price: 2.80,
        category: 'heisse-getraenke',
        tags: ['vegan', 'alkoholfrei'],
      },
      {
        id: 'kaffee-2',
        name: 'pott',
        description: 'kleiner kaffee, kräftig',
        price: 4.90,
        category: 'heisse-getraenke',
        tags: ['vegan', 'alkoholfrei'],
      },
      {
        id: 'kaffee-3',
        name: 'espresso',
        description: 'intensiver, kurzer kaffee',
        price: 2.40,
        category: 'heisse-getraenke',
        tags: ['vegan', 'alkoholfrei'],
      },
      {
        id: 'kaffee-4',
        name: 'doppelter espresso',
        description: 'extra kräftig',
        price: 3.50,
        category: 'heisse-getraenke',
        tags: ['vegan', 'alkoholfrei'],
      },
      {
        id: 'kaffee-5',
        name: 'latte macchiato',
        description: 'milchkaffee mit schaum',
        price: 3.50,
        category: 'heisse-getraenke',
        tags: ['vegetarisch', 'alkoholfrei'],
      },
      {
        id: 'kaffee-6',
        name: 'cappuccino',
        description: 'espresso mit aufgeschäumter milch',
        price: 3.50,
        category: 'heisse-getraenke',
        tags: ['vegetarisch', 'alkoholfrei'],
      },
      {
        id: 'kakao-1',
        name: 'kakao',
        description: 'heiße schokolade',
        price: 3.50,
        category: 'heisse-getraenke',
        tags: ['vegetarisch', 'alkoholfrei'],
      },
      {
        id: 'kakao-2',
        name: 'kakao mit sahne',
        description: 'heiße schokolade mit schlagsahne',
        price: 3.90,
        category: 'heisse-getraenke',
        tags: ['vegetarisch', 'alkoholfrei'],
      },
      {
        id: 'tee-1',
        name: 'glas tee',
        description: 'verschiedene sorten nach wahl',
        price: 2.80,
        category: 'heisse-getraenke',
        tags: ['vegan', 'alkoholfrei'],
      },
      {
        id: 'tee-2',
        name: 'frische minze',
        description: 'tee mit frischer minze',
        price: 3.50,
        category: 'heisse-getraenke',
        tags: ['vegan', 'alkoholfrei'],
      },
    ],
  },

  // cocktails
  {
    id: 'cocktails',
    title: 'cocktails',
    subtitle: 'alle 6,90 €, außer taxi driver',
    items: [
      {
        id: 'cocktail-1',
        name: 'vida',
        description: 'weißer rum, limetten, rohrzucker, maracuja',
        price: 6.90,
        category: 'cocktails',
        tags: ['vegan'],
      },
      {
        id: 'cocktail-2',
        name: 'filosofia',
        description: 'ouzo, grenadine, limonade, minze',
        price: 6.90,
        category: 'cocktails',
        tags: ['vegan'],
      },
      {
        id: 'cocktail-3',
        name: 'saudade',
        description: 'cachaça, limette, kokosnuss, ananas, maracuja, sahne, blue curaçao',
        price: 6.90,
        category: 'cocktails',
        tags: ['vegetarisch'],
      },
      {
        id: 'cocktail-4',
        name: 'frida',
        description: 'mango-tequila, limette, chilirand',
        price: 6.90,
        category: 'cocktails',
        tags: ['vegan', 'scharf'],
      },
      {
        id: 'cocktail-5',
        name: 'bohème',
        description: 'cognac, zitronensaft, orange, holunder',
        price: 6.90,
        category: 'cocktails',
        tags: ['vegan'],
      },
      {
        id: 'cocktail-6',
        name: 'libertá',
        description: 'amaretto, zitrone, grenadine, soda',
        price: 6.90,
        category: 'cocktails',
        tags: ['vegan'],
      },
      {
        id: 'cocktail-7',
        name: 'und tschüss',
        description: 'jägermeister, zitrone, apfelsaft, ginger ale',
        price: 6.90,
        category: 'cocktails',
        tags: ['vegan'],
      },
      {
        id: 'cocktail-8',
        name: 'taxi driver',
        description: 'alkoholfrei: rohrzucker, limette, maracuja',
        price: 5.90,
        category: 'cocktails',
        tags: ['vegan', 'alkoholfrei'],
      },
    ],
  },

  // longdrinks, aperitifs & wein
  {
    id: 'longdrinks-wein',
    title: 'longdrinks, aperitifs & wein',
    subtitle: 'erfrischende mischgetränke und ausgewählte weine',
    items: [
      {
        id: 'aperitif-1',
        name: 'hugo',
        description: 'prosecco, holunderblüte, minze, limette',
        price: 6.50,
        category: 'longdrinks-wein',
        tags: ['vegan'],
      },
      {
        id: 'aperitif-2',
        name: 'aperol spritz',
        description: 'aperol, prosecco, soda',
        price: 6.50,
        category: 'longdrinks-wein',
        tags: ['vegan'],
      },
      {
        id: 'longdrink-1',
        name: 'wodka lemon',
        description: 'wodka mit zitronenlimonade',
        price: 5.50,
        category: 'longdrinks-wein',
        tags: ['vegan'],
      },
      {
        id: 'longdrink-2',
        name: 'wodka orange',
        description: 'wodka mit orangensaft',
        price: 5.50,
        category: 'longdrinks-wein',
        tags: ['vegan'],
      },
      {
        id: 'longdrink-3',
        name: 'gin tonic',
        description: 'gin mit tonic water',
        price: 5.50,
        category: 'longdrinks-wein',
        tags: ['vegan'],
      },
      {
        id: 'longdrink-4',
        name: 'cuba libre',
        description: 'brauner rum mit cola und limette',
        price: 5.50,
        category: 'longdrinks-wein',
        tags: ['vegan'],
      },
      {
        id: 'longdrink-5',
        name: 'whisky cola',
        description: 'whisky mit coca-cola',
        price: 5.50,
        category: 'longdrinks-wein',
        tags: ['vegan'],
      },
      {
        id: 'wein-1',
        name: 'grauburguner (0,2l)',
        description: 'deutscher weißwein, trocken',
        price: 4.90,
        category: 'longdrinks-wein',
        tags: ['vegan'],
      },
      {
        id: 'wein-2',
        name: 'primitivo (0,2l)',
        description: 'italienischer rotwein, fruchtig',
        price: 4.90,
        category: 'longdrinks-wein',
        tags: ['vegan'],
      },
      {
        id: 'wein-3',
        name: 'cabernet sauvignon (0,2l)',
        description: 'kräftiger rotwein',
        price: 4.90,
        category: 'longdrinks-wein',
        tags: ['vegan'],
      },
      {
        id: 'wein-4',
        name: 'rosé (0,2l)',
        description: 'frischer roséwein',
        price: 4.90,
        category: 'longdrinks-wein',
        tags: ['vegan'],
      },
      {
        id: 'prosecco-1',
        name: 'villa sandi (0,1l)',
        description: 'italienischer schaumwein',
        price: 3.90,
        category: 'longdrinks-wein',
        tags: ['vegan'],
      },
      {
        id: 'prosecco-2',
        name: 'villa sandi (0,75l)',
        description: 'flasche prosecco',
        price: 25.00,
        category: 'longdrinks-wein',
        tags: ['vegan'],
      },
    ],
  },
];

export const specialMenus: MenuItem[] = [
  {
    id: 'special-1',
    name: 'la vida cuatro',
    description: '4 tapas nach wahl (exkl. ausnahmen), 1 portion brot',
    price: 19.90,
    category: 'angebote',
    tags: ['vegan', 'vegetarisch', 'fleisch', 'fisch'],
    isSpecial: true,
  },
  {
    id: 'special-2',
    name: 'la vida menü',
    description: '6 tapas nach wahl (exkl. ausnahmen), 1 flasche hauswein, 1 großes wasser, 2 portionen brot',
    price: 49.90,
    category: 'angebote',
    tags: ['vegan', 'vegetarisch', 'fleisch', 'fisch'],
    isSpecial: true,
  },
];

export const filterTags: { id: MenuTag; label: string; color: string }[] = [
  { id: 'vegan', label: 'vegan', color: '#002500' },
  { id: 'vegetarisch', label: 'vegetarisch', color: '#1a4a1a' },
  { id: 'fleisch', label: 'fleisch', color: '#740E14' },
  { id: 'fisch', label: 'fisch', color: '#A7754D' },
  { id: 'scharf', label: 'scharf', color: '#070E0F' },
  { id: 'glutenfrei', label: 'glutenfrei', color: '#A7754D' },
  { id: 'alkoholfrei', label: 'alkoholfrei', color: '#740E14' },
];
